import { MouseEvent, Dispatch, SetStateAction } from 'react';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import { ExtendedVaultMatterModel } from './vault-export-page';
import NoDataOverlay from '../../grid-overlay/no-data-overlay';
import { formatBytes, toCamelCase } from '../../../utils/vault-utils';
import { useUserSettings } from '../../../utils/use-user-settings';
import {
  isAbleToSeeUIElement,
  VaultRoles,
} from '../../../utils/roles/client-roles';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import Add from '@mui/icons-material/Add';
import Close from '@mui/icons-material/Close';
import MoreVert from '@mui/icons-material/MoreVert';
import Search from '@mui/icons-material/Search';
import TableRows from '@mui/icons-material/TableRows';
import ViewModule from '@mui/icons-material/ViewModule';
import Visibility from '@mui/icons-material/Visibility';
import SharedButton from '../../../shared/buttons/shared-button';
import { useTheme } from '@mui/material/styles';

export interface VaultMattersTileViewProps {
  isPageLoading: boolean;
  paginatedMatters: ExtendedVaultMatterModel[];
  searchTerm: string;
  setSearchTerm: (value: string) => void;
  setTilePage: (value: number) => void;
  setSelectedMatterId: (id: string) => void;
  setOpenAddMatterDialog: Dispatch<SetStateAction<boolean>>;
  setOpenAddVaultExport: Dispatch<SetStateAction<boolean>>;
  matterView: 'tile' | 'table';
  setMatterView: Dispatch<SetStateAction<'tile' | 'table'>>;
  handleOpenActionMenu: (
    event: MouseEvent<HTMLElement>,
    matterId: string
  ) => void;
  handleSelectMatter: (matterId: string) => void;
  canCreateMatter?: boolean;
}

export default function VaultMattersTileView({
  isPageLoading,
  paginatedMatters,
  searchTerm,
  setSearchTerm,
  setTilePage,
  setSelectedMatterId,
  setOpenAddMatterDialog,
  setOpenAddVaultExport,
  matterView,
  setMatterView,
  handleOpenActionMenu,
  handleSelectMatter,
  canCreateMatter,
}: Readonly<VaultMattersTileViewProps>) {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const cardBorder = isDarkMode
    ? `0.5px solid ${theme.palette.semantic.outline.light}`
    : 0;
  const { toolsRoles } = useUserSettings();
  const canCreateVaultExport =
    isAbleToSeeUIElement(toolsRoles, VaultRoles.CreateVaultExports.value) ||
    [
      VaultRoles.CreateGmailVaultExport,
      VaultRoles.CreateChatVaultExport,
      VaultRoles.CreateGeminiVaultExport,
      VaultRoles.CreateCalendarVaultExport,
      VaultRoles.CreateDriveVaultExport,
      VaultRoles.CreateGroupsVaultExport,
    ].some((permission) => isAbleToSeeUIElement(toolsRoles, permission.value));

  return (
    <>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{
          bgcolor: theme.palette.semantic.surface.bg1,
          p: 2,
          pl: 3,
          borderRadius: 0,
          border: cardBorder,
          borderBottom: 0,
          boxShadow: `0 1px 6px 0 ${theme.palette.semantic.boxShadow}`,
        }}
      >
        <TextField
          size="small"
          variant="outlined"
          placeholder="Search..."
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setTilePage(0);
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment
                position="start"
                sx={{
                  mr: 0.5,
                }}
              >
                <Search
                  fontSize="small"
                  sx={{
                    ml: -0.5,
                    color: (theme) => theme.palette.text.secondary,
                  }}
                />
              </InputAdornment>
            ),
            endAdornment: searchTerm ? (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={() => {
                    setSearchTerm('');
                    setTilePage(0);
                  }}
                  edge="end"
                >
                  <Close fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
          sx={{
            width: 300,
            ml: -1,
            backgroundColor: searchTerm
              ? 'transparent'
              : (theme) => theme.palette.background.default,
            '& .MuiOutlinedInput-root': {
              backgroundColor: searchTerm
                ? 'transparent'
                : (theme) => theme.palette.background.default,
              borderRadius: (theme) => `${theme.shape.borderRadius}px`,

              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: (theme) => theme.palette.text.secondary,
              },

              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: (theme) => theme.palette.text.secondary,
              },

              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: (theme) => theme.palette.primary.main,
                borderWidth: 2,
              },
            },
            '& .MuiInputBase-input::placeholder': {
              color: (theme) => theme.palette.text.secondary,
              opacity: 1,
            },
            borderRadius: 0.75,
          }}
        />
        <Stack direction="row" spacing={2} alignItems="center">
          <Tooltip
            arrow
            title={
              !canCreateMatter
                ? `You do not have the "${VaultRoles.CreateVaultMatters.name}" permission.`
                : ''
            }
          >
            <span>
              <SharedButton
                startIcon={<Add />}
                variant="primary"
                disabled={!canCreateMatter}
                onClick={() => {
                  setSelectedMatterId('');
                  setOpenAddMatterDialog(true);
                }}
              >
                Create Matter
              </SharedButton>
            </span>
          </Tooltip>
          <ToggleButtonGroup
            value={matterView}
            exclusive
            onChange={(_, value) => value && setMatterView(value)}
            size="small"
          >
            <ToggleButton value="tile" aria-label="Tile view">
              <ViewModule />
            </ToggleButton>
            <ToggleButton value="table" aria-label="Table view">
              <TableRows />
            </ToggleButton>
          </ToggleButtonGroup>
        </Stack>
      </Stack>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          my: 0,
          p: 2,
          height: '100%',
          backgroundColor: theme.palette.semantic.surface.bg2,
          border: cardBorder,
          borderTop: 0,
          boxShadow: `0 1px 6px 0 ${theme.palette.semantic.boxShadow}`,
        }}
      >
        <Grid container spacing={3}>
          {isPageLoading ? (
            Array.from({ length: 6 }).map((_, idx) => (
              <Grid size={{ xs: 12, md: 4 }} key={`skeleton-${idx}`}>
                <Card
                  sx={{
                    minHeight: 150,
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                    borderRadius: 2,
                    boxShadow: 1,
                  }}
                >
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      mb={1}
                    >
                      <Box sx={{ width: '100%' }}>
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          mb={0.5}
                        >
                          <Skeleton
                            variant="circular"
                            width={20}
                            height={20}
                            sx={{ mr: 1 }}
                          />
                          <Skeleton variant="text" width="60%" height={24} />
                        </Stack>
                        <Skeleton variant="text" width="80%" height={20} />
                      </Box>
                    </Stack>

                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      mb={1}
                      sx={{ mt: 1 }}
                    >
                      <Skeleton variant="circular" width={10} height={10} />
                      <Skeleton variant="text" width="20%" height={16} />
                    </Stack>
                    <Divider sx={{ mb: 1 }} />
                    <Stack direction="row" spacing={12}>
                      <Box>
                        <Skeleton variant="text" width={50} height={16} />
                        <Skeleton variant="text" width={30} height={24} />
                      </Box>
                      <Box>
                        <Skeleton variant="text" width={60} height={16} />
                        <Skeleton variant="text" width={40} height={24} />
                      </Box>
                    </Stack>
                    <Divider sx={{ mt: 1 }} />
                  </CardContent>
                  <CardActions
                    sx={{
                      justifyContent: 'space-between',
                      gap: 1.5,
                      width: '100%',
                      px: 1,
                      pb: 1,
                      mt: -1,
                    }}
                  >
                    <Skeleton
                      variant="rectangular"
                      height={30}
                      sx={{ flex: 1, borderRadius: 1 }}
                    />
                    <Skeleton
                      variant="rectangular"
                      height={30}
                      sx={{ flex: 1, borderRadius: 1 }}
                    />
                  </CardActions>
                </Card>
              </Grid>
            ))
          ) : paginatedMatters.length === 0 ? (
            <Grid size={{ xs: 12 }}>
              <Box
                sx={{
                  height: 300,
                  borderRadius: 1,
                  overflow: 'hidden',
                }}
              >
                <NoDataOverlay
                  title="No Vault Matters Found"
                  icon={<Visibility />}
                  message="Try updating your search or filters."
                />
              </Box>
            </Grid>
          ) : (
            paginatedMatters.map((matter) => {
              const exportCount = matter.exportCount ?? 0;
              const exportsRunning = matter.exportsRunning ?? 0;
              const exportsFailed = matter.exportsFailed ?? 0;
              const statusColor =
                matter.state === 'OPEN'
                  ? 'success.main'
                  : matter.state === 'CLOSED'
                  ? 'text.secondary'
                  : 'error.main';

              return (
                <Grid size={{ xs: 12, md: 4 }} key={matter.matterId}>
                  <Card
                    sx={{
                      minHeight: 220,
                      display: 'flex',
                      flexDirection: 'column',
                      height: '100%',
                      borderRadius: 2,
                      boxShadow: 1,
                    }}
                  >
                    <CardContent sx={{ flexGrow: 1 }}>
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="flex-start"
                        mb={1}
                      >
                        <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                            mb={0.5}
                            sx={{ minWidth: 0 }}
                          >
                            <FolderOutlinedIcon
                              color="primary"
                              sx={{
                                fontSize: 20,
                                flexShrink: 0,
                                mr: 1,
                              }}
                            />
                            <Typography
                              variant="subtitle1"
                              noWrap
                              title={matter.name}
                              sx={{ minWidth: 0 }}
                            >
                              <strong>{matter.name}</strong>
                            </Typography>
                          </Stack>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            title={matter.description}
                            sx={{
                              minWidth: 0,
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                          >
                            {matter.description || 'No description'}
                          </Typography>
                        </Box>
                        <IconButton
                          edge="end"
                          onClick={(event) =>
                            handleOpenActionMenu(event, matter.matterId)
                          }
                          size="small"
                        >
                          <MoreVert />
                        </IconButton>
                      </Stack>

                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        mb={1}
                      >
                        <Box
                          sx={{
                            width: 10,
                            height: 10,
                            borderRadius: '50%',
                            bgcolor: statusColor,
                          }}
                        />
                        <Typography variant="caption" color="text.secondary">
                          {toCamelCase(matter.state)}
                        </Typography>
                      </Stack>
                      <Divider sx={{ mb: 1 }} />
                      <Stack direction="row" spacing={12}>
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Exports
                          </Typography>
                          <Stack
                            direction="row"
                            alignItems="center"
                            spacing={1}
                          >
                            <Typography variant="body1">
                              <strong>{exportCount}</strong>
                            </Typography>
                            {exportCount > 0 && (
                              <Box
                                sx={{
                                  width: 8,
                                  height: 8,
                                  borderRadius: '50%',
                                  bgcolor:
                                    exportsFailed > 0
                                      ? 'error.main'
                                      : exportsRunning > 0
                                      ? 'info.main'
                                      : 'success.main',
                                }}
                              />
                            )}
                          </Stack>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Data Size
                          </Typography>
                          <Typography variant="body1">
                            <strong>{formatBytes(matter.dataSizeBytes)}</strong>
                          </Typography>
                        </Box>
                      </Stack>
                      <Divider sx={{ mt: 1 }} />
                    </CardContent>
                    <CardActions
                      sx={{
                        justifyContent: 'space-between',
                        gap: 1.5,
                        width: '100%',
                        px: 2,
                        pb: 2,
                        mt: -1,
                      }}
                    >
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() => handleSelectMatter(matter.matterId)}
                        sx={{ flex: 1 }}
                      >
                        View
                      </Button>
                      <Tooltip
                        title={
                          !canCreateVaultExport
                            ? `You do not have the "${VaultRoles.CreateVaultExports.name}" permission.`
                            : ''
                        }
                      >
                        <span style={{ flex: 1, display: 'inline-flex' }}>
                          <Button
                            size="small"
                            variant="contained"
                            disabled={!canCreateVaultExport}
                            onClick={() => {
                              setSelectedMatterId(matter.matterId);
                              setOpenAddVaultExport(true);
                            }}
                            sx={{ width: '100%' }}
                          >
                            Create Export
                          </Button>
                        </span>
                      </Tooltip>
                    </CardActions>
                  </Card>
                </Grid>
              );
            })
          )}
        </Grid>
      </Box>
    </>
  );
}
